import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

function Dot({ ok }) {
  return ok
    ? <CheckCircle2 className="w-4 h-4 text-green-600" />
    : <XCircle className="w-4 h-4 text-red-500" />;
}

export default function EmailDiagnostics() {
  const [status, setStatus] = useState(null);
  const [to, setTo] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/admin/email-status").then(({ data }) => { setStatus(data); setTo(data.admin_notify_email || ""); }).catch(() => {});
  }, []);

  const sendTest = async () => {
    setSending(true); setResult(null);
    try {
      const { data } = await api.post("/admin/test-email", { to });
      setResult(data);
      if (data.ok) toast.success(`Testmail verzonden naar ${data.to}`);
      else toast.error("Verzenden mislukt — zie details.");
    } catch (e) {
      setResult({ ok: false, error: e.response?.data?.detail || e.message });
    } finally { setSending(false); }
  };

  return (
    <div data-testid="email-diagnostics" className="bg-white border border-slate-200 rounded-md p-5 mb-6">
      <h3 className="font-display font-bold text-slate-900 flex items-center gap-2 mb-1"><Mail className="w-4 h-4 text-orange-500" /> E-mail diagnostiek</h3>
      <p className="text-xs text-slate-500 mb-4">Controleer of het e-mailproces correct geconfigureerd is in deze omgeving.</p>
      {status && (
        <div className="grid sm:grid-cols-2 gap-2 text-sm mb-4">
          <div className="flex items-center gap-2"><Dot ok={status.resend_key_set} /> Resend API-sleutel {status.resend_key_set ? "ingesteld" : "ONTBREEKT"}</div>
          <div className="flex items-center gap-2"><Dot ok={!!status.sender_email} /> Afzender: <span className="font-mono text-xs">{status.sender_email || "—"}</span></div>
          <div className="flex items-center gap-2"><Dot ok={!!status.admin_notify_email} /> Admin-melding: <span className="font-mono text-xs">{status.admin_notify_email || "—"}</span></div>
          <div className="flex items-center gap-2"><Dot ok={!!status.app_url} /> App-URL: <span className="font-mono text-xs">{status.app_url || "—"}</span></div>
        </div>
      )}
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[220px]">
          <label className="text-xs font-bold text-slate-700">Stuur een testmail naar</label>
          <Input data-testid="test-email-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder="jij@voorbeeld.be" className="rounded-sm border-slate-300" />
        </div>
        <Button data-testid="send-test-email-btn" onClick={sendTest} disabled={sending || !to}
          className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm font-bold">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verstuur testmail"}
        </Button>
      </div>
      {result && (
        <div data-testid="test-email-result" className={`mt-3 rounded-sm px-3 py-2 text-xs ${result.ok ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
          {result.ok
            ? `✓ Verzonden naar ${result.to} (id: ${result.id}). Controleer ook je spam-map.`
            : `✗ ${result.error}`}
        </div>
      )}
    </div>
  );
}
